<template>
<canvas
    :class='gridClasses()'
    :ref='uniqueCanvasId + renderingKey'
    :width='canvasWidth'
    :height='canvasHeight'
    @pointerdown='canvasMouseDown'
    @pointerup='canvasMouseUp'
    @pointermove='canvasMouseMove'
    @pointerout='canvasMouseOut'/>
</template>
<!--
    @touchstart.self='onTouchStart'
    @touchend.self='onTouchEnd'
    @touchmove.self='onTouchMove'
-->
<script>

import gridUtils from '../mixins/gridUtils.js'
import utilityService from '../mixins/utilityService.js'
import deepCopyService from '../mixins/deepCopyService.js'

export default {
    // https://stackoverflow.com/questions/40410332/vuejs-access-child-components-data-from-parent
    // https://stackoverflow.com/questions/45478035/start-range-in-v-for-n-in-10-from-zero

    mixins: [gridUtils, utilityService, deepCopyService],
    props: {
        cellPixelSize: {
            type: Number,
            required: true
        },
        borderThicknessPixels: {
            type: Number,
            required: true
        },
        borderColor: {
            type: String,
            required: true
        },
        cellSelectedColor: {
            type: String,
            required: true
        },
        cellNotSelectedColor: {
            type: String,
            required: true
        },
        rowMajorArray: { // threading, tieup, treadling
            type: Array,
            required: false
        },
        copiedCellsArray: { // threading, tieup, treadling copied region
            type: Array,
            required: false
        },
        selectEntireColumns: { // tieup: multiple threads (shafts) can be selected in a column (false), threading: only one at a time (true)
            type: Boolean,
            required: true
        },
        selectEntireRows: { // tieup: multiple threads (shafts) can be selected in a column (false), treadling: only one at a time (true)
            type: Boolean,
            required: true
        },
        selectedRowNumbersList: {
            type: Array,
            required: false
        },
        selectedColumnNumbersList: {
            type: Array,
            required: false
        },
        allowClearIndividualCells: { // allows individual cells to be toggled and cleared even when selectEntireColumns or selectEntireRows is true
            type: Boolean,
            default: false
        },
        allowDeleteMultipleRowsOrColumns: { // allows multiple rows or columns to be selected for deletion, otherwise only one may be selected for deletion
            type: Boolean,
            default: false
        },
        selectColumnOnClick: { // clicking returns a cell for selection at its column
            type: Boolean,
            default: false
        },
        selectRowOnClick: { // clicking returns a row for selection at its row
            type: Boolean,
            default: false
        },
        deleteColumnOnClick: { // clicking returns a cell for deletion at its column
            type: Boolean,
            default: false
        },
        deleteRowOnClick: { // clicking returns a row for deletion at its row
            type: Boolean,
            default: false
        },
        clearColumnOnClick: { // clicking returns a cell for clearing at its column
            type: Boolean,
            default: false
        },
        clearRowOnClick: { // clicking returns a cell for clearing at its row
            type: Boolean,
            default: false
        },
        insertColumnOnClick: { // clicking returns a cell for insertion before or after the click position depending on which half of the cell is clicked
            type: Boolean,
            default: false
        },
        insertRowOnClick: { // clicking returns a cell for insertion before or after the click position depending on which half of the cell is clicked
            type: Boolean,
            default: false
        },
        isCopy: {
            type: Boolean,
            default: false
        },
        isPaste: {
            type: Boolean,
            default: false
        },
        insertOnPaste: {
            type: Boolean,
            default: false
        },
        pasteFlipHoriz: {
            type: Boolean,
            default: false
        },
        pasteFlipVert: {
            type: Boolean,
            default: false
        },
        pasteWrapRows: {
            type: Boolean,
            default: false
        },
        pasteWrapCols: {
            type: Boolean,
            default: false
        },
        uniqueCanvasId: {
            type: String,
            required: true
        },
        isReadOnly: {
            type: Boolean,
            required: true
        },
        showInverse: {
            type: Boolean,
            required: true
        },
        drawLeftToRight: {
            type: Boolean,
            required: true
        },
        drawTopToBottom: {
            type: Boolean,
            required: true
        },
        rowsRepresentShafts: {
            required: true
        },
        renderingKey: {
            type: String,
            required: true
        }
    },
    components: {
    },
    data() {
        return {
            dataGrid: [],
            vueCanvas: null,
            minimumCellPaddingPixelSize: 4,
            mouseDownRowNumber: -1,
            mouseDownColumnNumber: -1
        }
    },
    emits: ['gridDataChanged', 'gridSizeIncreased', 'gridCellSelected', 'cellsCopied'],
    methods: {
        renderGrid() {
            this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
            this.vueCanvas.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

            this.vueCanvas.fillStyle = this.borderColor
            for (let rowIndex=0; rowIndex <= this.rowsCount; rowIndex++) {
                this.vueCanvas.fillRect(0, rowIndex * this.totalCellHeight, this.canvasWidth, this.borderThicknessPixels)
            }

            for (let colIndex=0; colIndex <= this.columnsCount; colIndex++) {
                this.vueCanvas.fillRect(colIndex * this.totalCellWidth, 0, this.borderThicknessPixels, this.canvasHeight)
            }

            let cellSelectedColor = this.cellSelectedColor
            let cellNotSelectedColor = this.cellNotSelectedColor
            let borderThicknessPixels = this.borderThicknessPixels
            let cellPixelSize = this.cellPixelSize
            let minimumCellPaddingPixelSize = this.minimumCellPaddingPixelSize
            let rowsCount = this.rowsCount
            let columnsCount = this.columnsCount

            for (let rowIndex=0; rowIndex < rowsCount; rowIndex++) {
                let cellY = this.pixelYFromRowNum(rowIndex) + borderThicknessPixels
                for (let colIndex=0; colIndex < columnsCount; colIndex++) {
                    let cellX = this.pixelXFromColNum(colIndex) + borderThicknessPixels

                    let value = this.dataGrid[rowIndex][colIndex]
                    let color = null
                    if (!this.showInverse) {
                        color = (value ? cellSelectedColor : cellNotSelectedColor)
                    }
                    else {
                        color = (!value ? cellSelectedColor : cellNotSelectedColor)
                    }

                    let cellWidth = cellPixelSize
                    let cellHeight = cellPixelSize

                    let horizOffsetAmount = 0
                    let vertOffsetAmount = 0

                    if (cellPixelSize > minimumCellPaddingPixelSize) { // put a little space around the black cell centers for good looks
                        horizOffsetAmount = 1
                        vertOffsetAmount = 1
                    }

                    this.vueCanvas.fillStyle=color
                    this.vueCanvas.fillRect(cellX + horizOffsetAmount, cellY + vertOffsetAmount, cellWidth - (horizOffsetAmount * 2), cellHeight - (vertOffsetAmount * 2))
                }
            }
            if (this.selectedRowNumbersList) {
                if (this.selectedRowNumbersList.length > 0) {
                    this.highlightSelectedRows()
                }
            }
            if (this.selectedColumnNumbersList) {
                if (this.selectedColumnNumbersList.length > 0) {
                    this.highlightSelectedColumns()
                }
            }
        },

        canvasMouseDown(e) {
            // console.log('in canvas mouse down')
            this.mouseDownRowNumber = this.canvasRowNumber(e)
            this.mouseDownColumnNumber = this.canvasColumnNumber(e)
            // console.log('mouseDownRowNumber: ' + this.mouseDownRowNumber + ', mouseDownColumnNumber: ' + this.mouseDownColumnNumber)
        },

        canvasMouseUp(e) {
            // console.log('in mouseUp 0')
            // this.enableTouchScroll()
            if (e.offsetX >= 0 && e.offsetX <= this.canvasWidth && e.offsetY >= 0 && e.offsetY <= this.canvasHeight) {
                // console.log('in mouseUp 1')
                if (!this.isReadOnly) {
                    // console.log('in mouseUp 2')
                    let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
                    let canvasCurrentRect = canvasElement.getBoundingClientRect()
                    let canvasX = Math.trunc(e.clientX - canvasCurrentRect.left)
                    let canvasY = Math.trunc(e.clientY - canvasCurrentRect.top)

                    let rowNum = this.rowNumFromPixelY(canvasY)
                    let colNum = this.colNumFromPixelX(canvasX)

                    let roundedColNum = this.roundedColNumFromPixelX(canvasX)
                    let roundedRowNum = this.roundedRowNumFromPixelY(canvasY)

                    if (rowNum >= this.rowsCount) { // can happen if last dividing line is clicked after final cell
                        rowNum = this.rowsCount-1
                    }
                    if (colNum >= this.colsCount) { // can happen if last dividing line is clicked after final cell
                        colNum = this.colsCount-1
                    }
                    if (this.isCopy) {
                        // console.log('in canvasMouseUp isCopy')
                        let copyStartRow = Math.min(this.mouseDownRowNumber, rowNum)
                        let copyStartCol = Math.min(this.mouseDownColumnNumber, colNum)
                        let copyEndRow = Math.max(this.mouseDownRowNumber, rowNum)
                        let copyEndCol = Math.max(this.mouseDownColumnNumber, colNum)

                        // console.log('copyStartRow: ' + copyStartRow + ', copyStartCol: ' + copyStartCol + ', copyEndRow: ' + copyEndRow + ', copyEndCol: ' + copyEndCol)

                        this.copySelectedCells(copyStartRow, copyStartCol, copyEndRow, copyEndCol)
                        this.mouseDownRowNumber = -1
                        this.mouseDownColumnNumber = -1
                    }
                    else if (this.isPaste) {
                        // console.log('in canvaseMouseUp, isPaste')
                        console.log('rowsCount: ' + this.rowsCount + ', rowNum: ' + rowNum + ', roundedRowNum: ' + roundedRowNum)
                        console.log('colsCount: ' + this.columnsCount + ', colNum: ' + colNum + ', roundedColNum: ' + roundedColNum)
                        let pasteRow = rowNum
                        let pasteCol = colNum
                        console.log('before pasteRow: ' + pasteRow + ', pasteCol: ' + pasteCol)
                        if (this.insertOnPaste) {
                            if (roundedRowNum === this.rowsCount) {
                                pasteRow = this.rowsCount
                            }
                            if (roundedColNum === this.columnsCount) {
                                pasteCol = this.columnsCount
                            }
                        }
                        console.log('after pasteRow: ' + pasteRow + ', pasteCol: ' + pasteCol)
                        this.pasteSelectedCells(pasteRow, pasteCol, true)
                    }
                    else if (this.deleteRowOnClick || this.clearRowOnClick || this.selectRowOnClick) {
                        let selectedStartRow = Math.min(this.mouseDownRowNumber, rowNum)
                        let selectedEndRow = Math.max(this.mouseDownRowNumber, rowNum)
                        let rowsCount = selectedEndRow - selectedStartRow + 1
                        if (this.rowsCount > 1) {
                            this.$emit('gridCellSelected', { 'rowIndex': selectedStartRow, 'colIndex': colNum, 'count': rowsCount }) // lets the parent know so that the row(s) or column(s) can be deleted
                        }
                    }
                    else if (this.deleteColumnOnClick || this.clearColumnOnClick || this.selectColumnOnClick) {
                        let selectedStartCol = Math.min(this.mouseDownColumnNumber, colNum)
                        let selectedEndCol = Math.max(this.mouseDownColumnNumber, colNum)
                        let colsCount = selectedEndCol - selectedStartCol + 1
                        if (this.columnsCount > 1) {
                            this.$emit('gridCellSelected', { 'rowIndex': rowNum, 'colIndex': selectedStartCol, 'count': colsCount }) // lets the parent know so that the row(s) or column(s) can be deleted
                        }
                    }
                    else if (this.insertRowOnClick || this.insertColumnOnClick) {
                        // insert happens halfway between cells, so see which half of the cell vertically and horizontally were clicked
                        // and return the index of the cell for each where the insert should happen
                        this.$emit('gridCellSelected', { 'rowIndex': roundedRowNum, 'colIndex': roundedColNum }) // lets the parent know so that the row or column can be deleted
                    }
                    else {
                        if (rowNum === this.mouseDownRowNumber && colNum === this.mouseDownColumnNumber) {
                            // console.log('in canvasMouseUp, toggle individual cell')
                            this.toggleCell(rowNum, colNum)
                        }
                        else {
                            // console.log('in canvasMouseUp, toggle line')
                            this.lineBetweenSelectedCells(this.mouseDownRowNumber, this.mouseDownColumnNumber, rowNum, colNum)
                        }
                    }
                }
                this.mouseDownRowNumber = -1
                this.mouseDownColumnNumber = -1
            }
        },

        canvasMouseMove(e) {
            // console.log('in canvasMouseMove, e: ' + JSON.stringify(e))
            // console.log('isCopy: ' + this.isCopy + ', isPaste: ' + this.isPaste)
            if (!this.isReadOnly) {
                if (this.insertColumnOnClick || this.insertRowOnClick) {
                    this.insertMouseMove(e)
                }
                else if (this.deleteRowOnClick || this.deleteColumnOnClick || this.clearRowOnClick || this.clearColumnOnClick || this.selectRowOnClick || this.selectColumnOnClick) {
                    // console.log('calling selectRowOrColumnMouseMove')
                    this.selectRowOrColumnMouseMove(e)
                }
                else if (this.isCopy) {
                    this.copyMouseMove(e)
                }
                else if (this.isPaste) {
                    this.pasteMouseMove(e)
                }
                else { // mouse is dragging with no intermediate mode, draw line points between start and end
                    this.normalMouseMove(e)
                }
            }
        },

        selectRowOrColumnMouseMove(e) {
            this.renderGrid() // erases any prior bounding box while moving
            console.log('inSelectRowOrColumnMouseMove')
            let row = this.canvasRowNumber(e)
            let col = this.canvasColumnNumber(e)
            if (this.deleteColumnOnClick || this.clearColumnOnClick || this.selectColumnOnClick) {
                if (col < this.columnsCount) {
                    if (this.mouseDownColumnNumber >= 0 && e.buttons === 1 && this.allowDeleteMultipleRowsOrColumns) {
                        this.drawBoundingBox(0, this.mouseDownColumnNumber, this.rowsCount, col)
                    }
                    else {
                        // console.log('selectRowOrColumnsMouseMove Column a')
                        this.drawBoundingBox(0, col, this.rowsCount, col)
                    }
                }
            }
            if (this.deleteRowOnClick || this.clearRowOnClick || this.selectRowOnClick) {
                if (row < this.rowsCount) {
                    if (this.mouseDownRowNumber >= 0 && e.buttons === 1 && this.allowDeleteMultipleRowsOrColumns) {
                        this.drawBoundingBox(this.mouseDownRowNumber, 0, row, this.columnsCount)
                    }
                    else {
                        // console.log('selectRowOrColumnsMouseMove Column b')
                        this.drawBoundingBox(row, 0, row, this.columnsCount)
                    }
                }
            }
        },

        insertMouseMove(e) {
            this.renderGrid() // erases any prior bounding box while moving
            let row = this.roundedCanvasRowNumber(e)
            let col = this.roundedCanvasColumnNumber(e)
            if (this.insertColumnOnClick) {
                this.drawBoundingBox(0, col, this.rowsCount, col)
            }
            if (this.insertRowOnClick) {
                this.drawBoundingBox(row, 0, row, this.columnsCount)
            }
        },

        copyMouseMove(e) {
            // console.log('in canvasMouseMove isCopy')
            if (this.pointIsOnGrid(this.mouseDownRowNumber, this.mouseDownColumnNumber) && e.buttons===1) {
                let currentItemRow = this.canvasRowNumber(e)
                let currentItemCol = this.canvasColumnNumber(e)
                this.renderGrid() // erases any prior bounding box while dragging
                // with no special modes, show the cells that will be colored as the mouse is dragged
                // console.log('grid, isCopy: ' + this.isCopy + ', isPaste: ' + this.isPaste + ' mouseDownRowNumber: ' + this.mouseDownRowNumber + ', mouseDownColumnNumber: ' + this.mouseDownColumnNumber)
                this.drawBoundingBox(this.mouseDownRowNumber, this.mouseDownColumnNumber, currentItemRow, currentItemCol)
            }
        },

        pasteMouseMove(e) {
            // console.log('in canvasMouseMove Paste, e: ' + JSON.stringify(e))
            // console.log('copiedCellsArray: ' + JSON.stringify(this.copiedCellsArray))
            this.renderGrid() // erases any prior bounding box while moving
            let startItemRow = this.canvasRowNumber(e)
            if (startItemRow < 0) {
                startItemRow = 0
            }

            let startItemColumn = this.canvasColumnNumber(e)
            if (startItemColumn < 0) {
                startItemColumn = 0
            }
            // console.log('startItemRow: ' + startItemRow)
            // console.log('startItemColumn: ' + startItemColumn)

            let endItemRow = startItemRow + (this.rowsRepresentShafts ? this.copiedCellsArray.length - 1 : this.copiedCellsArray[0].length-1)
            if (!this.pasteWrapRows) {
                if (endItemRow > this.rowsCount-1) {
                    endItemRow = this.rowsCount-1
                }
            }
            // console.log('endItemRow: ' + endItemRow)

            let endItemColumn = startItemColumn + (this.rowsRepresentShafts ? this.copiedCellsArray[0].length-1 : this.copiedCellsArray.length - 1)
            if (!this.pasteWrapCols) {
                if (endItemColumn > this.columnsCount - 1) {
                    endItemColumn = this.columnsCount - 1
                }
            }

            // console.log('endItemColumn: ' + startItemColumn)

            // console.log('in canvasMouseMove isPaste, startItemRow: ' + startItemRow + ', endItemRow: ' + endItemRow + 'startItemColumn: ' + startItemColumn + ', endItemColumn: ' + endItemColumn)
            // console.log('b')
            this.drawBoundingBox(startItemRow, startItemColumn, endItemRow, endItemColumn)
        },

        normalMouseMove(e) {
            if (this.pointIsOnGrid(this.mouseDownRowNumber, this.mouseDownColumnNumber) && e.buttons===1) {
                let currentItemRow = this.canvasRowNumber(e)
                let currentItemCol = this.canvasColumnNumber(e)
                if (this.pointIsOnGrid(currentItemRow, currentItemCol)) {
                    this.renderGrid() // erases any prior bounding box while dragging
                    // with no special modes, show the cells that will be colored as the mouse is dragged
                    this.drawBoundingBox(this.mouseDownRowNumber, this.mouseDownColumnNumber, currentItemRow, currentItemCol)
                }
            }
        },

        canvasMouseOut(e) {
            this.renderGrid() // erases any prior bounding box while dragging
        },

        // toggles individual cells only, not used for pasting or drag across lines of multiple cells
        toggleCell(rowIndex, colIndex) {
            if (this.pointIsOnGrid(rowIndex, colIndex)) {
                let toggledValue = !(this.dataGrid[rowIndex][colIndex])
                if (this.selectEntireRows) {
                    this.clearEntireRow(rowIndex, this.dataGrid)
                }
                if (this.selectEntireColumns) {
                    this.clearEntireColumn(colIndex, this.dataGrid)
                }
                let newValue = !(this.dataGrid[rowIndex][colIndex])
                if (!this.allowClearIndividualCells) {
                    this.dataGrid[rowIndex][colIndex] = newValue
                }
                else {
                    this.dataGrid[rowIndex][colIndex] = toggledValue
                }
                this.renderGrid()
                this.$emit('gridDataChanged', { 'rowIndex': rowIndex, 'colIndex': colIndex }) // lets the parent know so other draft components like the drawdown can be redrawn
            }
        },

        drawBoundingBox(startItemRow, startItemCol, endItemRow, endItemCol) {
            let rc = this.getOrderedRowsAndColumns(startItemRow, startItemCol, endItemRow, endItemCol)

            // console.log('in drawBounding Box, startItemRowIndex: ' + startItemRowIndex + ', endItemRowIndex: ' + endItemRowIndex + ', startItemColIndex: ' + startItemColIndex + ', endItemColIndex: ' + endItemColIndex)

            let gridArrayWithPaste = []
            let gridArrayWithOnlyPointsOnLine = []

            if (this.isPaste) {
                gridArrayWithPaste = this.getCellsGridCopyWithPasteApplied(rc.startItemRowIndex, rc.startItemColIndex, this.copiedCellsArray)
            }
            else if (!this.isIntermediateState) {
                if (startItemRow >= 0 && startItemCol >= 0 && endItemRow >= 0 && endItemCol >= 0) {
                    let pointsOnLine = this.determinePointsOnLine(startItemRow, startItemCol, endItemRow, endItemCol)
                    gridArrayWithOnlyPointsOnLine = this.getEmptyCellsGridWithPointsOnLineApplied(pointsOnLine)
                }
            }
            // console.log('in drawBounding Box, startItemRowIndex: ' + startItemRowIndex + ', endItemRowIndex: ' + endItemRowIndex + ', startItemColIndex: ' + startItemColIndex + ', endItemColIndex: ' + endItemColIndex)

            let startX = 0
            let startY = 0
            let width = 0
            let height = 0

            for (let rowNum = rc.startItemRowIndex; rowNum <= rc.endItemRowIndex; rowNum++) {
                for (let colNum = rc.startItemColIndex; colNum <= rc.endItemColIndex; colNum++) {
                    let row = rowNum % this.rowsCount
                    let col = colNum % this.columnsCount

                    startX = this.pixelXFromColNum(col) + this.borderThicknessPixels
                    startY = this.pixelYFromRowNum(row) + this.borderThicknessPixels

                    if (this.isPaste) {
                        // get the color for the cell for paste, it is the only mode which repaints the entire cell
                        if (gridArrayWithPaste[row][col]) {
                            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
                        }
                        else {
                            this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
                        }
                        height = this.cellPixelSize
                        width = this.cellPixelSize
                        this.vueCanvas.fillRect(startX, startY, width, height)
                        this.drawCellBoundingBox(startX, startY)
                    }

                    else if (this.deleteRowOnClick || this.clearRowOnClick || this.selectRowOnClick) {
                        this.drawCellBoundingBox(startX, startY)
                    }
                    else if (this.deleteColumnOnClick || this.clearColumnOnClick || this.selectColumnOnClick) {
                        this.drawCellBoundingBox(startX, startY)
                    }
                    else if (this.isCopy) {
                        this.drawCellBoundingBox(startX, startY)
                    }
                    else if (this.insertRowOnClick || this.insertColumnOnClick) {
                        if (this.insertRowOnClick && row === rc.endItemRowIndex) {
                            this.drawCellSeparatingLine(startX, startY)
                        }
                        else if (this.insertColumnOnClick && col === rc.endItemColIndex) {
                            this.drawCellSeparatingLine(startX, startY)
                        }
                    }
                    else { // no special mode, dragging fills in cells in a line between two points
                        // get the color for the cell for paste, it is the only mode which repaints the entire cell
                        if (gridArrayWithOnlyPointsOnLine[row][col]) {
                            this.vueCanvas.fillStyle = 'rgb(255, 0, 0)'
                        }
                        else {
                            if (this.dataGrid[row][col]) {
                                this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
                            }
                            else {
                                this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
                            }
                        }
                        height = this.cellPixelSize
                        width = this.cellPixelSize
                        if (this.cellPixelSize > 4) {
                            this.vueCanvas.fillRect(startX + 1, startY + 1, width - 2, height - 2)
                        }
                        else {
                            this.vueCanvas.fillRect(startX, startY, width, height)
                        }
                    }
                }
            }
            // console.log('drawBoundingBox startX: ' + startX + ', startY' + startY + ', width: ' + width + ', height: ' + height + ', startItemIndex: ' + startItemIndex + ', endItemIndex: ' + endItemIndex)
        },

        highlightSelectedColumns() {
            for (let index=0; index < this.selectedColumnNumbersList.length; index++) {
                let colNum = this.selectedColumnNumbersList[index]
                this.drawBoundingBox(0, colNum, this.rowsCount-1, colNum)
            }
        },

        highlightSelectedRows() {
            for (let index=0; index < this.selectedRowNumbersList.length; index++) {
                let rowNum = this.selectedRowNumbersList[index]
                this.drawBoundingBox(rowNum, 0, rowNum, this.columnsCount-1)
            }
        },

        getOrderedRowsAndColumns(startItemRow, startItemCol, endItemRow, endItemCol) {
            let result = {}
            if (startItemRow <= endItemRow) {
                result.startItemRowIndex = startItemRow
                result.endItemRowIndex = endItemRow
            }
            else {
                result.startItemRowIndex = endItemRow
                result.endItemRowIndex = startItemRow
            }

            if (startItemCol <= endItemCol) {
                result.startItemColIndex = startItemCol
                result.endItemColIndex = endItemCol
            }
            else {
                result.startItemColIndex = endItemCol
                result.endItemColIndex = startItemCol
            }
            return result
        },

        drawCellBoundingBox(startX, startY) {
            let lineX = 0
            let lineY = 0
            let lineWidth = 0
            let lineHeight = 0

            // top line
            lineX = startX - this.borderThicknessPixels
            lineY = startY - this.borderThicknessPixels
            lineWidth = this.cellPixelSize + (this.borderThicknessPixels * 2)
            lineHeight = this.borderThicknessPixels
            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(lineX, lineY, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, lineY + this.borderThicknessPixels, lineWidth - (this.borderThicknessPixels * 2), lineHeight)

            // left line
            lineX = startX - this.borderThicknessPixels
            lineY = startY - this.borderThicknessPixels
            lineWidth = this.borderThicknessPixels
            lineHeight = this.cellPixelSize + (this.borderThicknessPixels * 2)
            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(lineX, lineY, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, lineY + this.borderThicknessPixels, lineWidth, lineHeight- (this.borderThicknessPixels * 2))

            // bottom line
            lineX = startX - this.borderThicknessPixels
            lineY = startY + this.cellPixelSize
            lineWidth = this.cellPixelSize + (this.borderThicknessPixels * 2)
            lineHeight = this.borderThicknessPixels
            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(lineX, lineY, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, lineY - this.borderThicknessPixels, lineWidth - (this.borderThicknessPixels * 2), lineHeight)

            // right line
            lineX = startX + this.cellPixelSize
            lineY = startY - this.borderThicknessPixels
            lineWidth = this.borderThicknessPixels
            lineHeight = this.cellPixelSize + (this.borderThicknessPixels * 2)
            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(lineX, lineY, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(lineX - this.borderThicknessPixels, lineY + this.borderThicknessPixels, lineWidth, lineHeight - (this.borderThicknessPixels * 2))
        },

        drawCellSeparatingLine(startX, startY) {
            let lineX = 0
            let lineY = 0
            let lineWidth = 0
            let lineHeight = 0

            // top line
            if (this.insertRowOnClick) {
                lineX = startX - this.borderThicknessPixels
                lineY = startY - this.borderThicknessPixels
                if (!this.drawTopToBottom) {
                    lineY += this.cellPixelSize
                }
                lineWidth = this.cellPixelSize + (this.borderThicknessPixels * 2)
                lineHeight = this.borderThicknessPixels
                this.drawHorizontalSeparatingLines(lineX, lineY, lineWidth, lineHeight)
            }

            // left line
            if (this.insertColumnOnClick) {
                lineX = startX - this.borderThicknessPixels
                lineY = startY - this.borderThicknessPixels
                if (!this.drawLeftToRight) {
                    lineX += this.cellPixelSize
                }
                lineWidth = this.borderThicknessPixels
                lineHeight = this.cellPixelSize + (this.borderThicknessPixels * 2)
                this.drawVerticalSeparatingLines(lineX, lineY, lineWidth, lineHeight)
            }
        },

        drawHorizontalSeparatingLines(lineX, lineY, lineWidth, lineHeight) {
            // fix up first and last lines so they always show completely
            let adjustedY = lineY
            while (adjustedY + (this.borderThicknessPixels * 2) > this.canvasHeight-1) {
                adjustedY--
            }
            while (adjustedY - (this.borderThicknessPixels * 2) < 0) {
                adjustedY++
            }

            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(lineX, adjustedY, lineWidth, lineHeight)

            this.vueCanvas.fillStyle = this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, adjustedY + this.borderThicknessPixels, lineWidth, lineHeight)
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, adjustedY- this.borderThicknessPixels, lineWidth, lineHeight)

            this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, adjustedY + (this.borderThicknessPixels * 2), lineWidth, lineHeight)
            this.vueCanvas.fillRect(lineX + this.borderThicknessPixels, adjustedY - (this.borderThicknessPixels * 2), lineWidth, lineHeight)
        },

        drawVerticalSeparatingLines(lineX, lineY, lineWidth, lineHeight) {
            // fix up first and last lines so they always show completely
            let adjustedX = lineX
            while (adjustedX + (this.borderThicknessPixels * 2) > this.canvasWidth-1) {
                adjustedX--
            }
            while (adjustedX - (this.borderThicknessPixels * 2) < 0) {
                adjustedX++
            }
            this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
            this.vueCanvas.fillRect(adjustedX, lineY, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(223, 149, 149)'
            this.vueCanvas.fillRect(adjustedX + this.borderThicknessPixels, lineY + this.borderThicknessPixels, lineWidth, lineHeight)
            this.vueCanvas.fillRect(adjustedX - this.borderThicknessPixels, lineY + this.borderThicknessPixels, lineWidth, lineHeight)
            this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
            this.vueCanvas.fillRect(adjustedX + (this.borderThicknessPixels * 2), lineY, lineWidth, lineHeight)
            this.vueCanvas.fillRect(adjustedX - (this.borderThicknessPixels * 2), lineY, lineWidth, lineHeight)
        },

        canvasRowNumber(e) {
            let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
            let canvasCurrentRect = canvasElement.getBoundingClientRect()
            let canvasY = Math.trunc(e.clientY - canvasCurrentRect.top)
            let rowNum = this.rowNumFromPixelY(canvasY)
            return rowNum
        },

        canvasColumnNumber(e) {
            let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
            let canvasCurrentRect = canvasElement.getBoundingClientRect()
            let canvasX = Math.trunc(e.clientX - canvasCurrentRect.left)
            let colNum = this.colNumFromPixelX(canvasX)
            return colNum
        },

        roundedCanvasRowNumber(e) {
            let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
            let canvasCurrentRect = canvasElement.getBoundingClientRect()
            let canvasY = Math.trunc(e.clientY - canvasCurrentRect.top)
            let rowNum = this.roundedRowNumFromPixelY(canvasY)
            return rowNum
        },

        roundedCanvasColumnNumber(e) {
            let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
            let canvasCurrentRect = canvasElement.getBoundingClientRect()
            let canvasX = Math.trunc(e.clientX - canvasCurrentRect.left)
            let colNum = this.roundedColNumFromPixelX(canvasX)
            return colNum
        },

        pixelYFromRowNum(rowNum) {
            let displayRowNum = this.getDisplayGridIndex(rowNum, !this.drawTopToBottom, this.rowsCount)
            let pixelY = displayRowNum * this.totalCellHeight
            // console.log('rowsCount: ' + this.rowsCount + ' rowNum: ' + rowNum + ' displayRowNum: ' + displayRowNum + ' pixelY: ' + pixelY)
            return pixelY
        },

        pixelXFromColNum(colNum) {
            let displayColNum = this.getDisplayGridIndex(colNum, !this.drawLeftToRight, this.columnsCount)
            let pixelX = displayColNum * this.totalCellWidth
            return pixelX
        },

        gridClasses() {
            if (!this.isReadOnly) {
                return 'hand-cursor'
            }
            else {
                return ''
            }
        },

        rowNumFromPixelY(y) {
            let rowNum = Math.trunc(y / this.totalCellHeight)
            return this.getDisplayGridIndex(rowNum, !this.drawTopToBottom, this.rowsCount)
        },

        colNumFromPixelX(x) {
            let colNum = Math.trunc(x / this.totalCellWidth)
            return this.getDisplayGridIndex(colNum, !this.drawLeftToRight, this.columnsCount)
        },

        roundedColNumFromPixelX(x) { // rounds cell up or down based on whether click X is in the left or right half of the cell, used for insertion
            let colNum = Math.trunc(x / this.totalCellWidth)
            let cellMidpoint = this.columnMidpoint(colNum)
            if (x > cellMidpoint) {
                colNum++
            }
            let roundedCol = this.getDisplayGridIndex(colNum, !this.drawLeftToRight, this.columnsCount)
            if (!this.drawLeftToRight) {
                roundedCol++
            }
            return roundedCol
        },

        roundedRowNumFromPixelY(y) { // rounds cell up or down based on whether click Y is in the top or bottom half of the cell, used for insertion
            let rowNum = Math.trunc(y / this.totalCellHeight)
            let cellMidpoint = this.rowMidpoint(rowNum)
            if (y > cellMidpoint) {
                rowNum++
            }
            let roundedRow = this.getDisplayGridIndex(rowNum, !this.drawTopToBottom, this.rowsCount)
            if (!this.drawTopToBottom) {
                roundedRow++
            }
            return roundedRow
        },

        columnMidpoint(colNum) {
            let startX = colNum * this.totalCellWidth
            let midpoint = startX += (this.totalCellWidth / 2)
            return midpoint
        },

        rowMidpoint(rowNum) {
            let startY = rowNum * this.totalCellHeight
            let midpoint = startY += (this.totalCellHeight / 2)
            return midpoint
        },

        copySelectedCells(startRowIndex, startColumnIndex, endRowIndex, endColumnIndex) {
            if (startRowIndex >=0 && startColumnIndex >= 0 && endRowIndex >= 0 && endColumnIndex >= 0) {
                // console.log('in copySelectedCells startRowIndex: ' + startRowIndex + ', endRowIndex: ' + endRowIndex + 'startColumnIndex: ' + startColumnIndex + ', endColumnIndex: ' + endColumnIndex)
                let newCopiedCellsArray = []
                for (let r = startRowIndex; r <= endRowIndex; r++) {
                    let rowArray = []
                    for (let c = startColumnIndex; c <= endColumnIndex; c++) {
                        rowArray.push(this.dataGrid[r][c])
                    }
                    newCopiedCellsArray.push(rowArray)
                }
                this.renderGrid()
                // console.log('in copySelectedCells startRowIndex: ' + startRowIndex + ', endRowIndex: ' + endRowIndex + 'startColumnIndex: ' + startColumnIndex + ', endColumnIndex: ' + endColumnIndex + ' copiedCellsArray: ' + JSON.stringify(newCopiedCellsArray))
                let finalCellsArray = !this.rowsRepresentShafts ? this.rotate2dArray(newCopiedCellsArray) : newCopiedCellsArray

                if (finalCellsArray.length > 0) {
                    let cellsCopiedObj = {
                        copiedCellsArray: finalCellsArray
                    }
                    // console.log('cellsCopied: ' + JSON.stringify(cellsCopiedObj))
                    this.$emit('cellsCopied', cellsCopiedObj)
                }
            }
        },

        pasteSelectedCells(startRowIndex, startColumnIndex) {
            let copiedRowsCount = this.copiedCellsArray.length
            let copiedColumnsCount = this.copiedCellsArray[0].length

            console.log('copiedRowsCount: ' + copiedRowsCount + ', copiedColumnsCount: ' + copiedColumnsCount)

            if (this.insertOnPaste) {
                if (!this.rowsRepresentShafts) { // if rows don't represent shafts, add rows to expand treadling on paste
                    for (let r=0; r < copiedColumnsCount; r++) { // copied region never rotates, so columns are always the number of items to add
                        this.insert2dRowMajorGridRow(this.dataGrid, startRowIndex)
                    }
                }
                else { // if rows represent shafts, then add columns to expand threading on paste
                    for (let c=0; c < copiedColumnsCount; c++) { // copied region never rotates, so columns are always the number of items to add
                        this.insert2dRowMajorGridColumn(this.dataGrid, startColumnIndex)
                    }
                }
            }

            let overwritePasteCellsArray = this.getCellsGridCopyWithPasteApplied(startRowIndex, startColumnIndex, this.copiedCellsArray)
            for (let row = 0; row < this.rowsCount; row++) {
                for (let col=0; col < this.columnsCount; col++) {
                    this.dataGrid[row][col] = overwritePasteCellsArray[row][col]
                }
            }

            // console.log('dataGrid after values copy: ' + JSON.stringify(this.dataGrid))
            if (!this.insertOnPaste) {
                this.$emit('gridDataChanged', { startRowIndex: startRowIndex, startColumnIndex: startColumnIndex }) // lets the parent know so other draft components like the drawdown can be redrawn
            }
            else {
                let insertPosition = this.rowsRepresentShafts ? startColumnIndex : startRowIndex
                // copied region never rotates, so columns are always the number of items to add
                this.$emit('gridSizeIncreased', { position: insertPosition, count: copiedColumnsCount }) // lets the parent know so thread colors can be added and draft components like the drawdown can be redrawn
            }
        },

        lineBetweenSelectedCells(startRowIndex, startColumnIndex, endRowIndex, endColumnIndex) {
            let pointsOnLine = this.determinePointsOnLine(startRowIndex, startColumnIndex, endRowIndex, endColumnIndex) // returns object array { row: num, col: num }
            // console.log('pointsOnLine: ' + JSON.stringify(pointsOnLine))

            let cellsArrayWithLine = this.getCellsGridCopyWithPointsOnLineApplied(pointsOnLine)
            for (let row = 0; row < this.rowsCount; row++) {
                for (let col=0; col < this.columnsCount; col++) {
                    this.dataGrid[row][col] = cellsArrayWithLine[row][col]
                }
            }
            this.renderGrid()
            this.$emit('gridDataChanged', { startRowIndex: startRowIndex, startColumnIndex: startColumnIndex }) // lets the parent know so other draft components like the drawdown can be redrawn
        },

        getCellsGridCopyWithPasteApplied(startRowIndex, startColumnIndex, copiedCellsArrayBuffer) {
            // copiedCells and cellsArray are both in native order

            let newCellsGrid = this.deepCopy(this.dataGrid)
            let copiedCellsArray = !this.rowsRepresentShafts ? this.rotate2dArray(this.deepCopy(copiedCellsArrayBuffer)) : this.deepCopy(copiedCellsArrayBuffer)

            // let copiedCellsRowsCount = copiedCellsArray.length
            // let copiedCellsColumnsCount = copiedCellsArray[0].length

            // console.log('before flips copiedCellsArray: copiedCellsRowsCount: ' + copiedCellsRowsCount + ', copiedCellsColumnsCount: ' + copiedCellsColumnsCount)
            // this.print2dArray(copiedCellsArray)

            copiedCellsArray = this.getCellsGridCopyWithFlipsApplied(copiedCellsArray)

            let copiedCellsRowsCount = copiedCellsArray.length
            let copiedCellsColumnsCount = copiedCellsArray[0].length

            // console.log('after flips copiedCellsArray: copiedCellsRowsCount: ' + copiedCellsRowsCount + ', copiedCellsColumnsCount: ' + copiedCellsColumnsCount)
            // this.print2dArray(copiedCellsArray)

            for (let r = 0; r < copiedCellsRowsCount; r++) {
                // console.log('pasteWrapRows: ' + this.pasteWrapRows + ', rowsCount: ' + this.rowsCount)
                // console.log('pasteWrapCols: ' + this.pasteWrapCols + ', columnsCount: ' + this.columnsCount)
                let destRow = this.pasteWrapRows ? (r + startRowIndex) % this.rowsCount : (r + startRowIndex)
                // let destRow = (r + startRowIndex) % this.rowsCount
                for (let c=0; c < copiedCellsColumnsCount; c++) {
                    let destCol = this.pasteWrapCols ? (c + startColumnIndex) % this.columnsCount : (c + startColumnIndex)
                    // let destCol = (c + startColumnIndex) % this.columnsCount

                    if (destRow < this.rowsCount) {
                        if (destCol < this.columnsCount) {
                            let newCellValue = copiedCellsArray[r][c]
                            if (newCellValue) {
                                if (this.selectEntireColumns) {
                                    this.clearEntireColumn(destCol, newCellsGrid)
                                }
                                if (this.selectEntireRows) {
                                    this.clearEntireRow(destRow, newCellsGrid)
                                }
                            }
                            // console.log('r: ' + r + ', c: ' + c + ', destRow: ' + destRow + ', destCol: ' + destCol + ', rowsCount: ' + this.rowsCount + ', columnsCount: ' + this.columnsCount)
                            newCellsGrid[destRow][destCol] = newCellValue
                        }
                    }
                }
            }

            // console.log('newCellsGrid: ' + JSON.stringify(newCellsGrid))
            return newCellsGrid
        },

        getCellsGridCopyWithFlipsApplied(copiedCellsArrayBuffer) {
            let newCellsGrid = this.deepCopy(copiedCellsArrayBuffer)
            if (this.pasteFlipVert) {
                // console.log('at pasteFlipVert')
                newCellsGrid = newCellsGrid.reverse()
            }
            if (this.pasteFlipHoriz) {
                // console.log('at pasteFlipHoriz')
                newCellsGrid = this.rotate2dArray(newCellsGrid)
                newCellsGrid = newCellsGrid.reverse()
                newCellsGrid = this.rotate2dArray(newCellsGrid)
            }
            return newCellsGrid
        },

        getCellsGridCopyWithPointsOnLineApplied(pointsOnLineArray) {
            let newCellsGrid = this.deepCopy(this.dataGrid)
            for (let p = 0; p < pointsOnLineArray.length; p++) {
                let point = pointsOnLineArray[p]
                if (this.selectEntireRows || this.selectEntireColumns) { // threading or treadling, set the points on the line instead of toggling
                    this.set2dRowMajorGridPointWithBoundsChecking(point.row, point.col, true, newCellsGrid, this.selectEntireRows, this.selectEntireColumns)
                }
                else {
                    // console.log('toggling row: ' + point.row + ', col: ' + point.col)
                    this.toggle2dRowMajorGridPointWithBoundsChecking(point.row, point.col, newCellsGrid)
                }
            }
            return newCellsGrid
        },

        getEmptyCellsGridWithPointsOnLineApplied(pointsOnLineArray) {
            let rowsInGrid = this.dataGrid.length
            let columnsInGrid = this.dataGrid[0].length
            let newCellsGrid = []
            for (let row=0; row < rowsInGrid; row++) {
                let rowColumns = new Array(columnsInGrid)
                rowColumns.fill(false)
                newCellsGrid.push(rowColumns)
            }
            for (let p = 0; p < pointsOnLineArray.length; p++) {
                let point = pointsOnLineArray[p]
                this.set2dRowMajorGridPointWithBoundsChecking(point.row, point.col, true, newCellsGrid, this.selectEntireRows, this.selectEntireColumns)
            }
            return newCellsGrid
        },

        pointIsOnGrid(row, col) {
            return row > -1 && col > -1 && row < this.rowsCount && col < this.columnsCount
        }
    },

    watch: { // watchers are called on changes to data
        showInverse: function(newShowInverse, oldShowInverse) {
            this.renderGrid()
        }
    },
    computed: {
        canvasWidth: function() {
            return (this.columnsCount * this.cellPixelSize) + ((this.columnsCount+1) * (this.borderThicknessPixels))
        },
        canvasHeight: function() {
            return (this.rowsCount * this.cellPixelSize) + ((this.rowsCount+1) * (this.borderThicknessPixels))
        },
        rowsCount: function() {
            return this.rowMajorArray.length
        },
        columnsCount: function() {
            return this.rowMajorArray[0].length
        },
        totalCellHeight: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        },
        totalCellWidth: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        },
        isIntermediateState: function() {
            return this.insertColumnOnClick ||
                this.insertRowOnClick ||
                this.isCopy ||
                this.isPaste ||
                this.deleteColumnOnClick ||
                this.deleteRowOnClick ||
                this.clearColumnOnClick ||
                this.clearRowOnClick ||
                this.selectRowOnClick ||
                this.selectColumnOnClick
        }
    },
    created() {
        this.dataGrid = this.rowMajorArray // reference, changes original, which we want here as parent is notified through emit
    },
    mounted() {
        let c = this.$refs[this.uniqueCanvasId + this.renderingKey]
        this.disableTouchScroll(c)
        let ctx = c.getContext('2d')
        this.vueCanvas = ctx
        this.renderGrid()
    },
    updated() { // updated is called on changes to the DOM
        this.dataGrid = this.rowMajorArray
        this.renderGrid()
    }
}
</script>

<style scoped>
</style>
